/**
 * Starting a trial — plan §6.13.
 *
 * One column, one card, no nav: this is the first Rowted screen anybody sees,
 * and every control on it is either a question that has to be answered or the
 * button. The plans come from the platform's own settings, so the price on
 * this page and the price on the subscription are the same number from the
 * same place.
 *
 * No password field. The signer gets a set-password link like any invited
 * user, which keeps one credential path in the product instead of two and
 * means a password never crosses the one endpoint that has no session behind
 * it.
 */
import { useEffect, useState } from 'react'
import { ApiError } from '@rowted/shared'
import { request, setCsrfToken } from '../lib/api.js'
import { Button, Card, Tag } from '../components/primitives.js'
import { Field } from '../components/fields.js'
import { RowtedLogo } from '../frame/logo.js'
import { cn } from '../lib/cn.js'

interface Plans {
  currency: string
  trialDays: number
  cardRequired: boolean
  tiers: {
    key: string; name: string; baseCents: number; seatCents: number; includedSeats: number
  }[]
}

export function Signup() {
  const [plans, setPlans] = useState<Plans | null>(null)
  const [company, setCompany] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [tier, setTier] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<{ trialEndsAt: string; setPasswordUrl: string } | null>(null)

  useEffect(() => {
    request<Plans>('/signup/plans')
      .then((payload) => {
        setPlans(payload)
        // The middle one, which is the one most people want.
        setTier(payload.tiers[Math.min(1, payload.tiers.length - 1)]?.key ?? '')
      })
      .catch(() => setError('Our plans could not be loaded. Try again in a moment.'))
  }, [])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      setDone(await request<{ trialEndsAt: string; setPasswordUrl: string }>('/signup', {
        method: 'POST',
        body: { company, firstName, lastName, email, tier, timezone: guessTimezone() },
      }))
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work. Try again.')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <Shell>
        <h1 className="m-0 text-title">Check your email</h1>
        <p className="m-0 text-body text-text-muted">
          {company} is ready. Set a password and you are in — your trial runs until{' '}
          {new Date(done.trialEndsAt).toLocaleDateString(undefined, {
            month: 'short', day: 'numeric', year: 'numeric',
          })}.
        </p>
        {/*
          * The link, on screen. Email is not connected yet, and the same is
          * true of an invite — a link nobody was given is not access.
          */}
        <a href={done.setPasswordUrl} className="text-body font-semibold text-accent">
          Set your password
        </a>
      </Shell>
    )
  }

  return (
    <Shell>
      <h1 className="m-0 text-title">Start a trial</h1>
      <p className="m-0 text-body text-text-muted">
        {plans ? `${plans.trialDays} days. ` : ''}
        {plans?.cardRequired ? 'A card up front.' : 'No card.'}
      </p>

      <form onSubmit={submit} className="flex flex-col gap-3">
        <Field label="Company" value={company} onChange={setCompany} required autoFocus />
        {/*
          * First and last, not "Your name" — §0.10 of the admin plan: every
          * place a person is created asks for both, because `users.name` is
          * derived from the parts (migration 0066) and a single box means the
          * split is guessed rather than given.
          */}
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" value={firstName} onChange={setFirstName} required />
          <Field label="Last name" value={lastName} onChange={setLastName} required />
        </div>
        <Field label="Work email" type="email" value={email} onChange={setEmail} required />

        <div>
          <span className="mb-1 block text-sub text-text-muted">Plan</span>
          <div className="flex flex-col gap-2">
            {(plans?.tiers ?? []).map((plan) => (
              <button
                key={plan.key}
                type="button"
                aria-pressed={tier === plan.key}
                onClick={() => setTier(plan.key)}
                className={cn(
                  'flex items-baseline gap-2 rounded-card border p-3 text-left',
                  tier === plan.key
                    ? 'border-accent-line bg-accent-soft'
                    : 'border-line bg-surface hover:bg-hover',
                )}
              >
                <span className="text-cell font-semibold text-text">{plan.name}</span>
                <span className="text-sub text-text-muted">
                  {plan.includedSeats} seats included
                </span>
                <span className="tnum ml-auto text-cell text-text">
                  ${(plan.baseCents / 100).toLocaleString()}/mo
                </span>
              </button>
            ))}
          </div>
        </div>

        {error && (
          <p role="alert"
            className="m-0 rounded-button bg-danger-bg px-3 py-2 text-cell text-danger">
            {error}
          </p>
        )}

        <Button
          type="submit"
          variant="primary"
          size="big"
          disabled={busy || !plans || !company.trim() || !firstName.trim()
            || !lastName.trim() || !email.trim()}
        >
          {busy ? 'Setting you up…' : 'Start the trial'}
        </Button>
      </form>

      <p className="m-0 text-sub text-text-muted">
        Already have an account? <a href="/sign-in" className="text-accent">Sign in</a>.
      </p>
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-app items-center justify-center bg-bg p-4">
      <Card className="flex w-full max-w-[420px] flex-col gap-4 p-6">
        <div className="flex items-center gap-2">
          <RowtedLogo />
          <Tag>Trial</Tag>
        </div>
        {children}
      </Card>
    </div>
  )
}

/** Best effort. The tenant can change it later; a wrong guess is not fatal. */
function guessTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

/**
 * "Show me" — plan §6.13's demo signup.
 *
 * Three fields, because the sales inbox wants a name, an email and a rough
 * size, and because a fourth is a person who leaves. The wait is real — a
 * clone of a whole company takes a moment — so the button says what is
 * happening rather than spinning silently.
 */
export function DemoSignup() {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [size, setSize] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const made = await request<{ csrfToken: string }>('/signup/demo', {
        method: 'POST',
        body: {
          firstName, lastName, email,
          company: company || undefined, size: size || undefined,
        },
      })
      setCsrfToken(made.csrfToken)
      // A full load: the session is new and the app boots as that user.
      window.location.assign('/')
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work. Try again.')
      setBusy(false)
    }
  }

  return (
    <Shell>
      <h1 className="m-0 text-title">Have a look around</h1>
      <p className="m-0 text-body text-text-muted">
        Your own copy of a landscaping company with a year of real-looking work in it.
        Change anything you like — nothing leaves the building, and the copy is thrown
        away when you are done.
      </p>

      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" value={firstName} onChange={setFirstName} required autoFocus />
          <Field label="Last name" value={lastName} onChange={setLastName} required />
        </div>
        <Field label="Work email" type="email" value={email} onChange={setEmail} required />
        <Field label="Company" value={company} onChange={setCompany} />
        <Field label="How many crews?" value={size} onChange={setSize} />

        {error && (
          <p role="alert"
            className="m-0 rounded-button bg-danger-bg px-3 py-2 text-cell text-danger">
            {error}
          </p>
        )}

        <Button
          type="submit"
          variant="primary"
          size="big"
          disabled={busy || !firstName.trim() || !lastName.trim() || !email.trim()}
        >
          {busy ? 'Building your copy…' : 'Open the demo'}
        </Button>
      </form>

      <p className="m-0 text-sub text-text-muted">
        Ready to start for real? <a href="/signup" className="text-accent">Start a trial</a>.
      </p>
    </Shell>
  )
}
